import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import {
    Room as LiveKitRoom,
    RoomOptions, ExternalE2EEKeyProvider, VideoPresets,
    RoomEvent, RemoteTrack, RemoteTrackPublication,
    RemoteParticipant, LocalTrackPublication,
    LocalParticipant, Participant,
    DisconnectReason,
} from "livekit-client";

const logger = baseLogger.getChild("LiveKitRoomManager");

/**
 * Dati necessari per connettersi a una LiveKit Room con E2EE.
 */
export interface LiveKitData {
    lkUrl: string;
    lkToken: string;
    lkRoomManager: LiveKitRoomManager;
    roomId: string;
}

/**
 * Gestisce la creazione e la connessione di una LiveKit Room con E2EE e opzioni audio/video.
 * Può essere usata sia da MASTER che da PEER.
 */
export class LiveKitRoomManager {
    public room: LiveKitRoom | null = null;
    private keyProvider: ExternalE2EEKeyProvider | null = null;

    private readonly leaveCall: (/*liveKitRoom: LiveKitRoom | null*/) => void;
    private readonly onAddParticipant?: (participant: Participant) => void;
    private readonly onRemoveParticipant?: (participant: Participant) => void;

    /**
     * Costruttore.
     * @param leaveCall Callback chiamata quando si lascia la stanza LiveKit.
     */
    constructor(
        leaveCall: (/*liveKitRoom: LiveKitRoom | null*/) => void,
        onAddParticipant?: (participant: Participant) => void,
        onRemoveParticipant?: (participant: Participant) => void
    ) {
        this.leaveCall = leaveCall;
        this.onAddParticipant = onAddParticipant;
        this.onRemoveParticipant = onRemoveParticipant;
    }

    /**
     * Crea e connette una LiveKit Room.
     * @param url URL del server LiveKit
     * @param token Token di accesso LiveKit
     * @param e2eeKey Chiave E2EE (stringa)
     * @param options Opzioni aggiuntive (opzionale)
     */
    async connectRoom(
        url: string,
        token: string,
        e2eeKey: string,// | ArrayBuffer,
        options?: Partial<RoomOptions>
    ): Promise<LiveKitRoom> {
        logger.debug("Connecting to LiveKit room with E2EE (connectRoom)");
        // Configura E2EE
        this.keyProvider = new ExternalE2EEKeyProvider();

        // Opzioni base
        const roomOptions: RoomOptions = {
            publishDefaults: {
                videoSimulcastLayers: [VideoPresets.h540, VideoPresets.h216],
                red: false, //!e2eeEnabled,
            },
            adaptiveStream: { pixelDensity: 'screen' }, // true,
            dynacast: true,
            // e2ee: {
            encryption: {
                keyProvider: this.keyProvider,
                worker: new Worker(new URL('livekit-client/e2ee-worker', import.meta.url), { type: 'module' }),
            },
            // publishDefaults: {
            //     videoCodec: 'vp8',
            // },
            // audioCaptureDefaults: {
            //     echoCancellation: true,
            //     noiseSuppression: true,
            // },
            // videoCaptureDefaults: {
            //     resolution: VideoPresets.h720.resolution,
            // },
            ...options,
        };

        // Crea e connette la Room
        this.room = new LiveKitRoom(roomOptions);

        // Imposta la chiave E2EE
        await this.keyProvider.setKey(e2eeKey);
        // console.debug("[LiveKitRoomManager] E2EE key length:", typeof e2eeKey === "string" ? e2eeKey.length : e2eeKey.byteLength);

        // Abilita E2EE prima di connettersi
        await this.room.setE2EEEnabled(true);

        // pre-warm connection, this can be called as early as your page is loaded
        // await this.room.prepareConnection(url, token);

        // configura gli event handlers
        this.room
            //     .on(RoomEvent.TrackSubscribed, this.handleTrackSubscribed)
            //     .on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed)
            //     .on(RoomEvent.ActiveSpeakersChanged, this.handleActiveSpeakerChange)
            //     .on(RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished)
            .on(RoomEvent.EncryptionError, (error: Error) => {
                logger.error("Errore E2EE - Il worker potrebbe avere problemi:", error);
            })
            .on(RoomEvent.Disconnected, (disconnectReason: DisconnectReason | undefined) => {
                logger.debug("Room disconnected:", disconnectReason ? DisconnectReason[disconnectReason] : "unknown reason");
                this.handleDisconnect()
            })
            .on(RoomEvent.ParticipantEncryptionStatusChanged, (enabled, participant) => {
                console.debug(`[LiveKitRoomManager] E2EE status changed for participant ${participant?.identity}: ${enabled}`);
            })
            .on(RoomEvent.ParticipantConnected, (participant) => {
                logger.debug("Participant connected:", participant);
                if (this.onAddParticipant) this.onAddParticipant(participant);
            })
            .on(RoomEvent.ParticipantDisconnected, (participant) => {
                logger.debug("Participant disconnected:", participant);
                if (this.onRemoveParticipant) this.onRemoveParticipant(participant);
            });

        // connect to room
        await this.room.connect(url, token);

        logger.debug("LOCAL encrypted:", this.room.localParticipant.isEncrypted);

        //Attiva solo audio all'avvio
        await this.room.localParticipant.setMicrophoneEnabled(true);

        // publish local camera and mic tracks
        // await this.room.localParticipant.enableCameraAndMicrophone();

        // Disattiva camera e microfono all'avvio
        // await this.toggleCamera(false);
        // await this.toggleMicrophone(false);

        return this.room;
    }

    /**
     * Attiva/disattiva il microfono locale.
     * @param enable true per attivare, false per disattivare
     */
    async toggleMicrophone(enable: boolean): Promise<void> {
        if (!this.room) return;
        await this.room.localParticipant.setMicrophoneEnabled(enable);
    }

    /**
     * Attiva/disattiva la videocamera locale.
     * @param enable true per attivare, false per disattivare
     */
    async toggleCamera(enable: boolean): Promise<void> {
        if (!this.room) return;
        await this.room.localParticipant.setCameraEnabled(enable);
    }

    /**
     * Disconnette e pulisce la stanza LiveKit.
     */
    async disconnectRoom() {
        if (this.room) {
            try {
                await this.room.disconnect();
            } catch (error) {
                logger.error("Errore durante la disconnessione dalla stanza LiveKit:", error);
            }
            this.room = null;
        }
        this.keyProvider = null;
    }

    //#region Event Handlers
    /**
     * Gestisce la sottoscrizione di una traccia remota.
     * @param track La traccia remota sottoscritta.
     * @param publication La pubblicazione della traccia remota.
     * @param participant Il partecipante remoto che ha pubblicato la traccia. 
     */
    handleTrackSubscribed(
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
    ) {
        // if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
        //     // attach it to a new HTMLVideoElement or HTMLAudioElement
        //     const element = track.attach();
        //     parentElement.appendChild(element);
        // }
    }

    handleTrackUnsubscribed(
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
    ) {
        // remove tracks from all attached elements
        track.detach();
    }

    handleLocalTrackUnpublished(
        publication: LocalTrackPublication,
        participant: LocalParticipant,
    ) {
        // when local tracks are ended, update UI to remove them from rendering
        publication.track?.detach();
    }

    handleActiveSpeakerChange(speakers: Participant[]) {
        // show UI indicators when participant is speaking
    }

    handleDisconnect() {
        logger.debug("disconnected from room");
        try {
            this.leaveCall(/*this.room*/);
        } catch (error) {
            logger.error("Errore durante la gestione della disconnessione dalla stanza LiveKit:", error);
        }
    }

    //#endregion
}
